import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { warrantyPairToken, withoutPairMarkers } from '@theme/warranty-pair';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// Warranty add-on selection (PP-863). Module state instead of DOM attributes: the
// buy-box (tiles and add-to-cart button) gets replaced by section morphs, which would
// silently drop a DOM-based selection.
let warrantySelectorBound = false;
const warrantySelection = {
  id: /** @type {string | null} */ (null),
  sku: /** @type {string | null} */ (null),
  // PP-920: owning product of the tile block (scopes the express mirror fields).
  // The pair token of those fields is derived from variant, tier and line
  // properties (warranty-pair.js), so it needs no state here.
  productId: /** @type {string | null} */ (null),
};

// PP-906: products whose warranty-upsell section came back without tiers on this
// page view — repeated card add-to-carts of those must not fetch again.
const warrantyDrawerlessProducts = new Set();

/**
 * PP-987: the warranty sections are rendered for the customer type of the cart.
 * A customer who switches the type in the header and adds a product right away
 * would get the answer for the previous type (no tiers after a switch to business
 * customer), so the request waits until the selector has written the new type.
 * @returns {Promise<void>}
 */
function customerTypeWritten() {
  const selector = /** @type {any} */ (customElements.get('client-type-selector'));
  return Promise.resolve(selector?.whenCartTypeWritten?.()).then(
    () => undefined,
    () => undefined
  );
}

/**
 * PP-906: requests a warranty section for a product through the Section Rendering
 * API. Resolves to the raw section HTML or null. The variant is passed along so the
 * server renders the price and pairing data for the variant that is actually being
 * added. A customer type that is still on its way to the cart is awaited first
 * (customerTypeWritten).
 * @param {string | undefined} productUrl
 * @param {string | null} variantId
 * @param {string} section
 * @returns {Promise<string | null>}
 */
function requestWarrantySection(productUrl, variantId, section) {
  if (!productUrl) return Promise.resolve(null);
  let url = productUrl.split('?')[0];
  // Algolia hit URLs carry no locale prefix (Liquid's product.url does).
  const localePrefix = window.algoliaConfig?.localePrefix || '';
  if (localePrefix && !url.startsWith(`${localePrefix}/`)) url = `${localePrefix}${url}`;
  const params = new URLSearchParams({ sections: section });
  if (variantId) params.set('variant', variantId);
  return customerTypeWritten()
    .then(() => fetch(`${url}?${params.toString()}`))
    .then((response) => (response.ok ? response.json() : null))
    .then((sections) => sections?.[section] ?? null)
    .catch(() => null);
}

/**
 * PP-987: warranty tiers for the add-to-cart popup, requested through the Section
 * Rendering API (sections/warranty-upsell-inline.liquid). For adds the page holds
 * no rendered options for: the product forms of lists, and the wishlist page, whose
 * adds come from the wishlist app (se-wishlist-page-override.js). Resolves to the
 * options element, or to null when there is nothing to offer (feature off, B2C,
 * product without tiers).
 * @param {object} product
 * @param {string | undefined} product.productUrl
 * @param {string | undefined} product.productId
 * @param {string | null} product.variantId
 * @returns {Promise<HTMLElement | null> | null}
 */
export function fetchWarrantyPopupOptions({ productUrl, productId, variantId }) {
  if (!window.themeSettings?.warrantyWidgetEnabled) return null;
  if (document.documentElement.getAttribute('data-customer-type') === 'b2c') return null;
  if (!productUrl || !productId) return null;
  if (warrantyDrawerlessProducts.has(productId)) return null;

  return requestWarrantySection(productUrl, variantId, 'warranty-upsell-inline').then((html) => {
    if (!html) return null;
    const template = document.createElement('template');
    template.innerHTML = html;
    const options = template.content.querySelector('[data-warranty-upsell-options]');
    if (!(options instanceof HTMLElement)) {
      // B2C is a live state (the customer can switch back). Only a response
      // without tiers marks the product as permanently without tiers for this page.
      if (!template.content.querySelector('[data-warranty-b2c-blocked]')) {
        warrantyDrawerlessProducts.add(productId);
      }
      return null;
    }
    return options;
  });
}

/**
 * Re-applies the current selection to the buy-box controls: the active class of the
 * tiles and checklist lines (plus their checked state) and the value of the dropdown
 * (block setting "Tier Card Layout"). Section morphs re-render all of them.
 */
function syncWarrantyTileState() {
  document.querySelectorAll('.listed_warranty_options .warranty-link').forEach((link) => {
    const isActive =
      !!warrantySelection.id &&
      link instanceof HTMLElement &&
      link.dataset.variantId === warrantySelection.id;
    link.classList.toggle('active', isActive);
    if (link.getAttribute('role') === 'checkbox') {
      link.setAttribute('aria-checked', String(isActive));
    }
  });
  document.querySelectorAll('warranty-select-component').forEach((component) => {
    if (component instanceof WarrantySelectComponent) component.applySelection();
  });
}

/**
 * Line item properties a product form sends with its add-to-cart, without the
 * warranty markers (the flows that pair a warranty add those themselves).
 * @param {HTMLFormElement} form
 * @returns {Record<string, string>}
 */
function formLineProperties(form) {
  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of new FormData(form).entries()) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match && typeof value === 'string') properties[match[1]] = value;
  }
  return withoutPairMarkers(properties);
}

/**
 * PP-920: mirrors the buy-box warranty selection into hidden product-form fields so
 * accelerated checkout (PayPal, "More payment options") sells the warranty too. The
 * wallet button reads the surrounding form when clicked: the parent line gets the
 * pairing properties, the warranty travels as a documented additional item
 * (shopify-additional-items[1][...], indexed from 1) with the same pair token — the
 * express purchase then contains the identical two lines as the regular add-to-cart
 * flow and the cart transform function (PP-862) prices the warranty line from the
 * token as usual.
 *
 * The fields exist only while a tier is selected and only in the form(s) of the
 * product the tile block belongs to (data-product-id, never quick-add forms). The
 * regular add-to-cart flow is unaffected: with a selected tier it posts its own JSON
 * payload (the mirror fields are not part of that payload), without one the fields
 * are already removed. Buy-box morphs (variant switch) drop the injected inputs, so
 * the sync is re-run from the variant update hook; quantity changes re-sync via the
 * quantity-selector event.
 */
function syncWarrantyExpressFields() {
  document.querySelectorAll('product-form-component').forEach((component) => {
    if (!(component instanceof HTMLElement)) return;
    const form = component.querySelector('form');
    if (!form) return;

    const isQuickAdd =
      component.classList.contains('quick-add__product-form-component') ||
      component.closest('#quick-add-dialog') !== null;
    const active =
      !isQuickAdd &&
      !!warrantySelection.id &&
      !!warrantySelection.productId &&
      component.dataset.productId === warrantySelection.productId;

    if (!active) {
      form.querySelectorAll('input[data-warranty-express-field]').forEach((input) => {
        input.remove();
      });
      return;
    }

    const quantityInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="quantity"]')
    );
    const quantity = Number(quantityInput?.value || component.dataset.quantityDefault || '1') || 1;
    // Same token the regular add-to-cart flow writes (warranty-pair.js): derived
    // from the variant in the form, the tier and the other line properties.
    const variantInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="id"]')
    );
    const pairToken = warrantyPairToken(
      variantInput?.value ?? '',
      warrantySelection.id ?? '',
      formLineProperties(form)
    );

    /** @type {Record<string, string>} */
    const fields = {
      // Parent line: visible SKU property (order export / SAP) + hidden link token —
      // same shape the regular add-to-cart flow writes on the parent line.
      'properties[warrantySKU]': warrantySelection.sku ?? '',
      'properties[_warranty_pair_id]': pairToken,
      // Warranty line as an additional express checkout item.
      'shopify-additional-items[1][id]': warrantySelection.id ?? '',
      'shopify-additional-items[1][quantity]': String(quantity),
      // The wallet reader takes the line item properties of an additional item from
      // this ONE field and runs it through JSON.parse — the expected shape is the
      // key/value pair list it also builds for the main item. Nested bracket fields
      // (properties[key]) make that parse throw, which silently drops ALL additional
      // items from the express purchase.
      'shopify-additional-items[1][properties]': JSON.stringify([
        { key: '_warranty', value: 'true' },
        { key: '_warranty_pair_id', value: pairToken },
      ]),
    };

    for (const [name, value] of Object.entries(fields)) {
      let input = /** @type {HTMLInputElement | null} */ (
        form.querySelector(`input[data-warranty-express-field][name="${name}"]`)
      );
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.dataset.warrantyExpressField = 'true';
        form.append(input);
      }
      input.value = value;
    }
  });
}

/** Clears the buy-box warranty selection and removes its express mirror fields. */
function clearWarrantySelection() {
  warrantySelection.id = null;
  warrantySelection.sku = null;
  warrantySelection.productId = null;
  syncWarrantyTileState();
  syncWarrantyExpressFields();
}

// PP-920 QA: returning to the product page from an express checkout attempt must
// deselect the warranty tier. The selection only survives that round trip when the
// browser restores the old page from the back/forward cache (a fresh load starts
// with an empty selection anyway), so the departure is flagged when a click lands
// on the express button area (clicks inside its closed shadow root retarget to the
// host) and the selection is cleared when the flagged page is restored.
const EXPRESS_DEPARTED_KEY = 'warranty-express-departed';
// PP-960: checkouts entered through the express button area skip the cart, so the
// cart attributes the checkout tracking reads are missing on that path. A click in
// the area therefore also leaves a short-lived timestamp that the tracking uses to
// classify such a checkout as online.
const EXPRESS_CHECKOUT_INTENT_KEY = 'gh_express_checkout_intent';

document.addEventListener('click', (event) => {
  if (event.target instanceof Element && event.target.closest('.accelerated-checkout-block')) {
    try {
      sessionStorage.setItem(EXPRESS_DEPARTED_KEY, '1');
      sessionStorage.setItem(EXPRESS_CHECKOUT_INTENT_KEY, String(Date.now()));
    } catch {
      // Storage unavailable: the tier then simply stays selected after returning
      // and the tracking keeps its offline fallback.
    }
  }
});

window.addEventListener('pageshow', (event) => {
  let departed = false;
  try {
    departed = sessionStorage.getItem(EXPRESS_DEPARTED_KEY) === '1';
    if (departed) sessionStorage.removeItem(EXPRESS_DEPARTED_KEY);
  } catch {
    // Storage unavailable: nothing was flagged either.
  }
  if (event.persisted && departed) clearWarrantySelection();
});

// PP-920 QA: the express button skips the cart entirely (it neither adds to the
// cart nor carries existing cart lines), so it is only offered while the theme
// cart is empty. The initial state is rendered server-side by the block; this
// keeps it in step with every cart change on the client. The live count is read
// from the cart endpoint because cart update events carry deltas or no count at
// all depending on their source.
let expressVisibilityTimeout = 0;
function scheduleExpressCheckoutVisibilitySync() {
  if (!document.querySelector('.accelerated-checkout-block')) return;
  clearTimeout(expressVisibilityTimeout);
  expressVisibilityTimeout = setTimeout(() => {
    fetch('/cart.js')
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart) return;
        document.querySelectorAll('.accelerated-checkout-block').forEach((block) => {
          block.classList.toggle('accelerated-checkout-block--cart-filled', cart.item_count > 0);
        });
      })
      .catch(() => {});
  }, 150);
}
document.addEventListener(ThemeEvents.cartUpdate, scheduleExpressCheckoutVisibilitySync);
// Correct a potentially stale server-rendered state right away (cached page HTML
// can carry the cart state of an earlier visit).
scheduleExpressCheckoutVisibilitySync();

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} WarrantySelectRefs
 * @property {HTMLSelectElement[]} selects - Both copies of the dropdown (wide, compact).
 */

/**
 * Warranty dropdown next to the quantity field (snippets/warranty-select.liquid, theme
 * setting "Tier selection" = Dropdown). The change handler is declared on the selects
 * themselves (on:change="/selectTier"). The picked entry is the tier, the first entry
 * (no value) clears the selection. Same module state as a tile click, so the
 * add-to-cart flows do not care which control picked the tier. The product comes from
 * the block or the product form the dropdown sits in.
 *
 * @extends {Component<WarrantySelectRefs>}
 */
class WarrantySelectComponent extends Component {
  requiredRefs = ['selects'];

  connectedCallback() {
    super.connectedCallback();
    // Section morphs (variant switch, cart updates) re-render the dropdown: show the
    // tier picked before.
    this.applySelection();
  }

  /**
   * Change handler of both copies of the dropdown.
   * @param {Event} event
   */
  selectTier(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const option = select.selectedOptions[0];
    const variantId = option?.dataset.variantId || null;
    if (!variantId) {
      clearWarrantySelection();
      return;
    }
    const owner = this.closest('[data-product-id]');
    warrantySelection.id = variantId;
    warrantySelection.sku = option?.dataset.variantSku ?? null;
    warrantySelection.productId =
      owner instanceof HTMLElement ? (owner.dataset.productId ?? null) : null;
    syncWarrantyTileState();
    syncWarrantyExpressFields();
  }

  /** Sets both copies to the current selection, or to the entry without warranty. */
  applySelection() {
    const value = warrantySelection.id ?? '';
    for (const select of this.refs.selects ?? []) {
      const hasOption = Array.from(select.options).some((option) => option.value === value);
      select.value = hasOption ? value : '';
    }
  }
}

if (!customElements.get('warranty-select-component')) {
  customElements.define('warranty-select-component', WarrantySelectComponent);
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId)
      );
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (
      this.closest('product-form-component')
    );
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId)
    );
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** True while an add request from this form has not finished. */
  #addInFlight = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // WARRANTY SELECTOR
    this.#initWarrantySelector();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Warranty tier selection in the buy-box (PP-863). Delegated listener + module state:
   * section morphs (variant change, cart updates) replace the tile DOM, so neither the
   * listeners nor DOM attributes are a safe place for the selection. Scoped to the
   * buy-box tile list — the upsell drawer has its own handling. The dropdown next to
   * the quantity field is its own element (WarrantySelectComponent) that writes the
   * same state.
   */
  #initWarrantySelector() {
    if (warrantySelectorBound) return;
    warrantySelectorBound = true;

    document.addEventListener('click', (event) => {
      const link =
        event.target instanceof Element &&
        event.target.closest('.listed_warranty_options .warranty-link');
      if (!link) return;
      event.preventDefault();
      const alreadyActive = link.classList.contains('active');
      link
        .closest('.listed_warranty_options')
        ?.querySelectorAll('.warranty-link')
        .forEach((item) => item.classList.remove('active'));
      if (alreadyActive) {
        clearWarrantySelection();
        return;
      }
      link.classList.add('active');
      const tileBlock = link.closest('.warranty_addons_block');
      warrantySelection.id = link.dataset.variantId ?? null;
      warrantySelection.sku = link.dataset.variantSku ?? null;
      warrantySelection.productId =
        tileBlock instanceof HTMLElement ? (tileBlock.dataset.productId ?? null) : null;
      syncWarrantyTileState();
      syncWarrantyExpressFields();
    });

    // Re-apply the visual state after buy-box morphs (variant switch etc.). The morph
    // also drops the injected express mirror fields (PP-920) — recreate them.
    document.addEventListener(ThemeEvents.variantUpdate, () => {
      requestAnimationFrame(() => {
        syncWarrantyTileState();
        syncWarrantyExpressFields();
      });
    });

    // PP-920: keep the express warranty quantity in step with the buy-box quantity.
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, () => {
      if (warrantySelection.id) syncWarrantyExpressFields();
    });

    // PP-987: a switch to private customer hides the tiles (client-type-selector.js
    // stamps the html element). A tier picked before goes with them, together with
    // its express checkout fields.
    new MutationObserver(() => {
      if (
        warrantySelection.id &&
        document.documentElement.getAttribute('data-customer-type') === 'b2c'
      ) {
        clearWarrantySelection();
      }
    }).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-customer-type'],
    });
  }

  /**
   * PP-863 UC5 / PP-906: should the upsell drawer open after this add-to-cart?
   * Returns true/false when a drawer for this product is already on the page (PDP,
   * quick-add modal, or a drawer injected by an earlier list add-to-cart). Returns
   * 'fetch' in list contexts (slider, collection, search): whether the product has
   * warranty tiers is only known server-side, so the warranty-upsell section is
   * requested while the add runs (PP-906).
   */
  #shouldOpenWarrantyUpsell(selectedWarrantyId) {
    if (selectedWarrantyId) return false;
    const productId = this.dataset.productId;
    if (!productId) return false;
    const drawer = this.#findWarrantyDrawer(productId);
    if (drawer) {
      if (typeof (/** @type {any} */ (drawer).showDialog) !== 'function') return false;
      // PDP buy-box: the block mirrors the live B2B/B2C state via its classes.
      const block = document.querySelector('.warranty_addons_block');
      if (block && block.classList.contains('hide_warranty_blocks')) return false;
      // Injected drawers have no buy-box block: the client-type selector stamps
      // the html element when the customer switches the type without a reload.
      if (!block && document.documentElement.getAttribute('data-customer-type') === 'b2c') {
        return false;
      }
      // Dismissed once on this page view (data attribute set by warranty-addons.js)?
      if (drawer.dataset.warrantyOptout === 'true') return false;
      return true;
    }
    // Only list-context forms fall through to the fetch flow — the PDP buy-box
    // without a drawer means the product has no tiers or the feature is off.
    if (!this.#isListContextForm()) return false;
    if (!window.themeSettings?.warrantyWidgetEnabled) return false;
    if (document.documentElement.getAttribute('data-customer-type') === 'b2c') return false;
    if (!this.dataset.productUrl) return false;
    if (warrantyDrawerlessProducts.has(productId)) return false;
    return 'fetch';
  }

  /**
   * Card quick-add forms and the quick-add modal ("choose" flow of multi-variant
   * cards) count as list contexts: neither has a usable drawer of its own — the
   * modal's copy of the PDP drawer cannot render once the modal closed.
   */
  #isListContextForm() {
    return (
      this.classList.contains('quick-add__product-form-component') ||
      this.closest('#quick-add-dialog') !== null
    );
  }

  /** @param {string} productId */
  #findWarrantyDrawer(productId) {
    // Drawers inside the quick-add modal are ignored: the modal closes right after
    // the add, and a dialog nested in a closed dialog cannot render.
    const drawers = document.querySelectorAll(
      `dialog-component.warrantyUpsellDrawer[data-product-id="${CSS.escape(productId)}"]`
    );
    for (const drawer of drawers) {
      if (!drawer.closest('#quick-add-modal-content')) {
        return /** @type {HTMLElement} */ (drawer);
      }
    }
    return null;
  }

  /** @param {string | null} [variantId] */
  #openWarrantyUpsell(variantId) {
    const productId = this.dataset.productId;
    const drawer = productId ? this.#findWarrantyDrawer(productId) : null;
    if (drawer && typeof (/** @type {any} */ (drawer).showDialog) === 'function') {
      // Re-pair with the variant of this add — a drawer cached by an earlier add
      // may still point at another variant (addWarrantyForParent matches on it).
      if (variantId) drawer.dataset.parentVariantId = variantId;
      /** @type {any} */ (drawer).showDialog();
    }
  }

  /**
   * PP-906: requests the upsell drawer section for this form's product
   * (requestWarrantySection). Runs in parallel with the cart add; resolves to the
   * raw section HTML or null.
   * @param {string | null} variantId
   * @returns {Promise<string | null>}
   */
  #requestWarrantyDrawer(variantId) {
    return requestWarrantySection(this.dataset.productUrl, variantId, 'warranty-upsell');
  }

  /**
   * PP-987: stores that confirm an add-to-cart with the upsell popup (theme setting
   * "Enable upsell popup instead of cart drawer") offer the warranty tiers inside
   * that popup, the upsell drawer is not used there. Provides the tier options for
   * the added product while the add runs. Resolves to the options element, or to
   * null when there is nothing to offer (feature off, B2C, product without tiers).
   * The popup places and binds the element (atc-upsell-popup.js).
   *
   * Product page: the warranty block carries the options, rendered with its own
   * content settings. Product lists have no block, the options are requested
   * through the Section Rendering API (fetchWarrantyPopupOptions).
   * @param {string | null} variantId
   * @returns {Promise<HTMLElement | null> | null}
   */
  #requestWarrantyPopupOptions(variantId) {
    if (!window.themeSettings?.warrantyWidgetEnabled) return null;
    if (document.documentElement.getAttribute('data-customer-type') === 'b2c') return null;
    const productId = this.dataset.productId;
    if (!productId) return null;

    if (!this.#isListContextForm()) {
      // PDP buy-box: the tile block only exists for products with tiers and mirrors
      // the live B2B/B2C state via its classes.
      const block = document.querySelector(
        `.warranty_addons_block[data-product-id="${CSS.escape(productId)}"]`
      );
      if (!block || block.classList.contains('hide_warranty_blocks')) return null;

      // The prices inside the block belong to the variant it was rendered for. Any
      // other variant takes the request below.
      const source = block.querySelector(
        '[data-warranty-popup-source] [data-warranty-upsell-options]'
      );
      if (
        source instanceof HTMLElement &&
        (!variantId || source.dataset.parentVariantId === variantId)
      ) {
        // PP-1019: the block mirrors the state of this page, the cart decides. The
        // check runs while the add runs; a private cart gets no tiers and the page
        // follows the cart. A failed read keeps the page state (see the tier check
        // above): the tiers are offered, the cart render and the checkout catch a
        // warranty of a private customer.
        const options = /** @type {HTMLElement} */ (source.cloneNode(true));
        const Selector = /** @type {any} */ (customElements.get('client-type-selector'));
        if (!Selector?.cartTypeFromServer) return Promise.resolve(options);
        return Selector.cartTypeFromServer().then((/** @type {string | null} */ cartType) => {
          if (cartType !== 'b2c') return options;
          Selector.applyCartType?.('b2c');
          return null;
        });
      }
    }

    return fetchWarrantyPopupOptions({
      productUrl: this.dataset.productUrl,
      productId,
      variantId,
    });
  }

  /**
   * PP-906: turns the warranty-upsell section response into a live drawer. Injects
   * the markup once per product (the drawer stays in the DOM afterwards, which keeps
   * the per-page-view opt-out from PP-863 working for repeated adds) and loads the
   * drawer behavior on demand. Resolves to whether the drawer should open.
   * @param {Promise<string | null>} request
   * @param {string | null} variantId
   * @returns {Promise<boolean>}
   */
  async #resolveWarrantyDrawer(request, variantId) {
    const productId = this.dataset.productId;
    const html = await request;
    if (!html || !productId) return false;

    const template = document.createElement('template');
    template.innerHTML = html;
    let drawer = /** @type {HTMLElement | null} */ (
      template.content.querySelector('dialog-component.warrantyUpsellDrawer')
    );
    if (!drawer) {
      // B2C is a live state (the customer can switch back) — only a response
      // without tiers marks the product as permanently drawer-less for this page.
      if (!template.content.querySelector('[data-warranty-b2c-blocked]')) {
        warrantyDrawerlessProducts.add(productId);
      }
      return false;
    }

    // A parallel add of the same product may have injected the drawer already.
    const existing = this.#findWarrantyDrawer(productId);
    if (existing) {
      drawer = existing;
    } else {
      const container = document.createElement('div');
      container.dataset.warrantyRemoteFor = productId;
      container.append(...template.content.childNodes);
      document.body.append(container);
    }
    // Pair with the variant that was actually added (Algolia hits can add a
    // non-default variant; addWarrantyForParent matches on this id).
    if (variantId) drawer.dataset.parentVariantId = variantId;
    if (drawer.dataset.warrantyOptout === 'true') return false;

    try {
      await Promise.all([import('@theme/dialog'), import('@theme/warranty-addons')]);
    } catch (error) {
      console.error('Warranty upsell drawer could not be initialized:', error);
      return false;
    }
    // First import binds via the module's own init; later injections re-init here.
    document.dispatchEvent(new CustomEvent('warranty:drawer-injected'));
    return typeof (/** @type {any} */ (drawer).showDialog) === 'function';
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find(
      (item) => item.variant_id.toString() === variantIdInput.value.toString()
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (
      this.querySelector('quantity-selector-component')
    );
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source === 'product-form-component'
    )
      return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    // A second click while the add is still running would send another /cart/add.js.
    if (this.#addInFlight) return;

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return (
      new URL(window.location.href).searchParams.get('variant') ||
      this.refs.variantId?.value ||
      undefined
    );
  }

  /** @returns {number} */
  #getQuantity() {
    return (
      Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1
    );
  }

  /**
   * Buttons that submit this form, plus the sticky bar when this is the main
   * product form. Buttons already disabled (sold out) are left alone so the
   * unlock does not make them clickable.
   * @returns {HTMLButtonElement[]}
   */
  #addToCartButtons() {
    /** @type {HTMLButtonElement[]} */
    const buttons = [];

    this.querySelectorAll('add-to-cart-component').forEach((container) => {
      const button = /** @type {AddToCartComponent} */ (container).refs?.addToCartButton;
      if (button instanceof HTMLButtonElement) buttons.push(button);
    });

    if (!this.classList.contains('quick-add__product-form-component') && this.dataset.productId) {
      const sticky = document.querySelector(
        `sticky-add-to-cart[data-product-id="${CSS.escape(this.dataset.productId)}"] [ref="addToCartButton"]`
      );
      if (sticky instanceof HTMLButtonElement) buttons.push(sticky);
    }

    return buttons;
  }

  /** Disables the buttons this request is allowed to re-enable. */
  #lockAddButtons() {
    for (const button of this.#addToCartButtons()) {
      if (button.disabled) continue;
      button.disabled = true;
      button.dataset.addInFlight = 'true';
      button.setAttribute('aria-busy', 'true');
    }
  }

  /**
   * The variant is unavailable, so the button must stay disabled after the add.
   * Leaving the request marker would make #endAddRequest enable it.
   */
  #clearAddInFlightMarker() {
    for (const button of this.#addToCartButtons()) {
      if (button.dataset.addInFlight !== 'true') continue;
      delete button.dataset.addInFlight;
      button.removeAttribute('aria-busy');
    }
  }

  /** Ignores further clicks until #endAddRequest. */
  #beginAddRequest() {
    this.#addInFlight = true;
    this.#lockAddButtons();
  }

  /** Re-enables only the buttons this request disabled. */
  #endAddRequest() {
    this.#addInFlight = false;

    for (const button of this.#addToCartButtons()) {
      if (button.dataset.addInFlight !== 'true') continue;
      button.disabled = false;
      delete button.dataset.addInFlight;
      button.removeAttribute('aria-busy');
    }

    // Clicks during a variant change are queued. If that queue was handed over
    // while this request was still running, send it now.
    if (this.#addToCartQueue.length === 0) return;
    const queuedItems = [...this.#addToCartQueue];
    this.#addToCartQueue = [];
    this.#processBatchAddToCart(queuedItems);
  }

  /** @returns {string | null} */
  #getProductHandle() {
    const productUrl = this.dataset.productUrl || '';
    const match = productUrl.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  /** @returns {number | null} */
  #getProductPrice() {
    const price = Number(this.dataset.productPrice);
    return Number.isFinite(price) ? price : null;
  }

  /** @returns {string | null} */
  #getAlgoliaCurrency() {
    return (
      window.algoliaConfig?.currency ||
      window.Shopify?.currency?.active ||
      window.theme?.currency ||
      window.Currency?.currentCurrency ||
      null
    );
  }

  /** @param {number} quantity */
  #trackAlgoliaAddToCart(quantity) {
    if (!window.aa || !window.algoliaConfig?.indexName) return;
    const variantId =
      this.refs.variantId?.value ||
      this.querySelector('input[name="id"]')?.value ||
      this.dataset.variantId;
    if (!variantId) return;

    const index = window.algoliaConfig.indexName;
    const queryID =
      this.getAttribute('data-query-id') ||
      this.getAttribute('data-queryid') ||
      this.dataset.queryId ||
      new URL(window.location.href).searchParams.get('queryID');
    const objectIDs = [variantId.toString()];
    const price = this.#getProductPrice();
    const currency = this.#getAlgoliaCurrency() || 'EUR';
    const objectData = [
      {
        quantity,
        ...(price !== null ? { price } : {}),
      },
    ];
    const selectedRecord = {
      index,
      objectIDs,
      ...(queryID ? { queryID } : {}),
      currency,
    };

    if (queryID) {
      window.aa('addedToCartObjectIDsAfterSearch', {
        eventName: 'Add To Cart',
        ...selectedRecord,
        objectData,
      });
    } else {
      window.aa('addedToCartObjectIDs', {
        eventName: 'Add To Cart',
        ...selectedRecord,
        objectData,
      });
    }

    this.#storeAlgoliaPurchase(selectedRecord);
  }

  /** @param {{index: string, objectIDs: string[], queryID?: string, currency?: string}} payload */
  #storeAlgoliaPurchase(payload) {
    try {
      const storageKey = 'algolia_analytics_purchase_objects';
      const stored = localStorage.getItem(storageKey);
      const items = stored ? JSON.parse(stored) : [];
      items.push(payload);
      localStorage.setItem(storageKey, JSON.stringify(items));
    } catch (error) {
      console.error('Failed to store Algolia purchase data:', error);
    }
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  async #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace(
          '{{ maximum }}',
          validation.maxQuantity?.toString() || ''
        );
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    // Held until the add promise settles, including the cart-quantity refresh below.
    // Set before the first await so a second click cannot start another add.
    this.#beginAddRequest();

    // PP-1019: the page can be older than the cart (switch to private customer in
    // another tab, page restored from the browser cache after a switch in the
    // checkout), so a picked tier is checked against the cart itself right before the
    // add. A private cart drops the tier, the page follows the cart and the customer
    // sees the warranty notice. One cart request, only when a tier is picked. Before
    // the form is read: dropping the tier also removes its express mirror fields
    // (PP-920) from the form, they must not travel with the add either. A failed
    // read keeps the page state on purpose: a business customer must not lose a
    // picked tier to a passing network error; a warranty that slips into a private
    // cart that way is removed by the cart render and blocked in the checkout.
    try {
      if (warrantySelection.id && !this.classList.contains('quick-add__product-form-component')) {
        const Selector = /** @type {any} */ (customElements.get('client-type-selector'));
        const cartType = Selector?.cartTypeFromServer ? await Selector.cartTypeFromServer() : null;
        if (cartType === 'b2c') {
          clearWarrantySelection();
          Selector.applyCartType?.('b2c');
          Selector.announceWarrantyRemoval?.(this.#getQuantity());
        }
      }
    } catch (error) {
      console.error(error);
      this.#endAddRequest();
      return;
    }

    const formData = new FormData(form);
    formData.set('attributes[order_type]', 'online');
    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    // Warranty add-on (PP-863): when a tier is selected in the buy-box, parent and
    // warranty are added in ONE documented JSON items request (the flat form format
    // does not support multiple items reliably). Both lines share a unique
    // _warranty_pair_id token — the cart transform function (PP-862) prices the warranty
    // line from it, and the unique token keeps identical warranty variants from
    // merging across different parent products.
    // The buy-box tile selection belongs to the PDP product only — a card quick-add
    // (slider on a PDP) must not attach the tier picked for the main product.
    const isCardQuickAdd = this.classList.contains('quick-add__product-form-component');
    // PP-987: warranties are for business customers only. A tier that is still
    // picked when a private customer adds the product stays out of the cart.
    if (
      warrantySelection.id &&
      document.documentElement.getAttribute('data-customer-type') === 'b2c'
    ) {
      clearWarrantySelection();
    }
    const warrantyId = isCardQuickAdd ? null : warrantySelection.id;
    const warrantySku = isCardQuickAdd ? null : warrantySelection.sku;
    // PP-987: with the add-to-cart popup the tiers are offered inside that popup,
    // so no upsell drawer opens and the popup shows up right after the add.
    const offersWarrantyInPopup = window.themeSettings?.enableUpsellPopup === true;
    let willOpenWarrantyUpsell = offersWarrantyInPopup
      ? false
      : this.#shouldOpenWarrantyUpsell(warrantyId);
    // PP-906, list contexts: request the drawer section in parallel with the add so
    // the open/skip decision is ready right after the add completes.
    const warrantyDrawerRequest =
      willOpenWarrantyUpsell === 'fetch'
        ? this.#requestWarrantyDrawer(formData.get('id')?.toString() ?? null)
        : null;
    // A warranty picked in the buy-box travels with the product; that add skips
    // the popup and opens the cart drawer (atc-upsell-popup.js).
    const warrantyPopupOptions =
      offersWarrantyInPopup && !warrantyId
        ? this.#requestWarrantyPopupOptions(formData.get('id')?.toString() ?? null)
        : null;

    // Line item properties of this add; the popup finds the added units by them.
    const addedProperties = formLineProperties(form);

    // Pair token of this add, set when a tier was picked in the buy-box.
    /** @type {string | null} */
    let warrantyRef = null;

    let fetchCfg;
    if (warrantyId) {
      warrantyRef = warrantyPairToken(
        String(formData.get('id') ?? ''),
        warrantyId,
        addedProperties
      );
      const parentQuantity = Number(
        formData.get('quantity')?.toString() || this.dataset.quantityDefault || '1'
      );

      // Parent line: visible SKU property (order export / SAP) + hidden link token.
      /** @type {Record<string, string>} */
      const parentProperties = {
        ...addedProperties,
        warrantySKU: warrantySku ?? '',
        _warranty_pair_id: warrantyRef,
      };

      const parentItem = {
        id: Number(formData.get('id')),
        quantity: parentQuantity,
        properties: parentProperties,
      };
      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) parentItem.selling_plan = Number(sellingPlan);

      const payload = {
        attributes: { order_type: 'online' },
        items: [
          parentItem,
          {
            id: Number(warrantyId),
            quantity: parentQuantity,
            properties: { _warranty: 'true', _warranty_pair_id: warrantyRef },
          },
        ],
        sections: cartItemComponentsSectionIds.join(','),
        sections_url: window.location.pathname,
      };
      fetchCfg = fetchConfig('json', { body: JSON.stringify(payload) });
    } else {
      fetchCfg = fetchConfig('javascript', { body: formData });
    }

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute('id') || '',
              response.message,
              response.description,
              response.errors
            )
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // /cart.js only refreshes the quantity selector. The confirmation popup
          // listens for the event below, so the refresh must not delay it.
          const cartQuantityUpdate = this.#fetchAndUpdateCartQuantity();

          const quantity =
            Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
          this.#trackAlgoliaAddToCart(quantity);
          const productHandle = this.#getProductHandle();

          // PP-906: in list contexts the drawer arrives with the parallel section
          // request — resolve it now so warrantyUpsellPending is a real boolean
          // before the cart components react to the event.
          if (warrantyDrawerRequest) {
            willOpenWarrantyUpsell = await this.#resolveWarrantyDrawer(
              warrantyDrawerRequest,
              formData.get('id')?.toString() ?? null
            );
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              productHandle,
              sections: response.sections,
              variantId: id.toString(),
              // Cart drawer stays closed while the warranty upsell drawer takes over;
              // it opens right after the upsell is answered (warranty-addons.js).
              warrantyUpsellPending: willOpenWarrantyUpsell === true,
              // PP-987: tier options for the add-to-cart popup, plus the warranty
              // this add already carries so the popup shows that tier as picked.
              warrantyPopupOptions,
              warrantyPairId: warrantyRef,
              warrantyVariantId: warrantyId,
              warrantyAddedWithProduct: Boolean(warrantyId),
              addedProperties,
            })
          );

          // Deselect the warranty tile once the add succeeded (PP-863 QA): the module
          // state survives back-forward navigation, so a stale selection would silently
          // re-attach the warranty on the next add to cart. Also removes the express
          // mirror fields (PP-920) so a later wallet click ships only the main product.
          if (warrantyId) {
            clearWarrantySelection();
          }

          if (willOpenWarrantyUpsell === true) {
            this.#openWarrantyUpsell(formData.get('id')?.toString() ?? null);
          }

          await cartQuantityUpdate;
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#endAddRequest();
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;
    if (this.#addInFlight) {
      this.#addToCartQueue.push(...items);
      return;
    }
    this.#beginAddRequest();

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      attributes: { order_type: 'online' },
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cartQuantityUpdate = this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        const productHandle = this.#getProductHandle();
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            productHandle,
            sections: response.sections,
          })
        );

        await cartQuantityUpdate;
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#endAddRequest();
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement(
        'beforebegin',
        /** @type {Element} */ (newElement.cloneNode(true))
      );
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
      if (event.detail.data.newProduct.url) {
        this.dataset.productUrl = event.detail.data.newProduct.url;
      }
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const {
      addToCartButtonContainer: currentAddToCartButtonContainer,
      acceleratedCheckoutButtonContainer,
    } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (
      !currentAddToCartButtonContainer ||
      (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)
    )
      return;

    // Update the button state
    const variantUnavailable =
      event.detail.resource == null || event.detail.resource.available == false;
    if (variantUnavailable) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector(
      'product-form-component [ref="addToCartButton"]'
    );
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (this.#addInFlight) {
      // No replacement button (Algolia out-of-stock alternatives) leaves the
      // in-flight marker on the disabled control. Drop it so the add cannot
      // enable a variant that is no longer available. An available variant is
      // locked again because morph replaces the marker.
      if (variantUnavailable) this.#clearAddInFlightMarker();
      else this.#lockAddButtons();
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
      if (typeof event.detail.resource.price === 'number') {
        this.dataset.productPrice = String(event.detail.resource.price / 100);
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector(
            'quantity-selector-component input[ref="quantityInput"]'
          )
        );

        if (
          this.refs.quantitySelector?.updateConstraints &&
          newQuantityInputElement &&
          currentQuantityValue
        ) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(
          currentElement,
          event.detail.data.html.querySelector(selector),
          fallback
        );
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(
      currentVolumePricing,
      newVolumePricing,
      this.refs.productFormButtons
    );

    const hasB2BFeatures =
      quantityRules ||
      newQuantityRules ||
      pricePerItem ||
      newPricePerItem ||
      currentVolumePricing ||
      newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
